// Server-side identity for Dash0 telemetry: stamps reservation/property/
// guest/profile identifiers on the active OTel span and keeps them in an
// AsyncLocalStorage store that the logger merges into every subsequent log
// record of the same async chain.
//
// The canonical call sites are the entity-resolving data functions
// (fetchTrip, findCheckin, getProfile, getProfileCache, …): every route that
// loads an entity is then identified without per-page code. URL-derived ids
// are stamped up front by the owning route segment — an App Router segment
// layout (trips/[id]/layout.tsx) or the top of getServerSideProps — so even
// requests that fail during data loading are identified; pages stamp only
// ids their segment cannot know (e.g. search params).
// The full rules live in src/utils/observability/README.md.
//
// Mechanics that make the data-layer placement work: a call before the first
// await of an async function (or anywhere in a sync function) executes in the
// caller's synchronous execution, so the store it creates seeds the caller's
// whole chain; calls after an await merge into that store object, and the
// merge is visible to every task sharing it.
//
// Span attributes are the reliable channel — filtering any span by
// numa.reservation.id surfaces the whole trace in Dash0. The log context is
// best-effort: React may render sibling server components in async tasks
// forked before any store existed, and those tasks keep the earlier (empty)
// store. Logs always carry trace_id, so they still correlate via the
// enriched span.

import { IdentityIds, toIdentityAttributes } from './identity-attributes';
import { trace } from './otel-trace';

// Guarded require (same pattern as otel-trace.ts) so bundlers drop the module
// from client bundles; every server context (the proxy included) runs on the
// Node runtime, where node:async_hooks is available.
const identityStore =
  typeof window === 'undefined'
    ? new (require('node:async_hooks') as typeof import('node:async_hooks')).AsyncLocalStorage<Record<string, string>>()
    : undefined;

/**
 * Call from request-scoped server code (getServerSideProps, server
 * components, API routes) as soon as each identifier is known; repeated calls
 * merge. Undefined ids are skipped, so optional values can be passed as-is.
 */
export const setRequestIdentifiers = (ids: IdentityIds): void => {
  const attributes = toIdentityAttributes(ids);
  if (Object.keys(attributes).length === 0) return;

  trace?.getActiveSpan()?.setAttributes(attributes);

  const store = identityStore?.getStore();
  if (store) {
    Object.assign(store, attributes);
    return;
  }
  try {
    // enterWith (instead of run) because callers set identifiers mid-request
    // without owning a wrapping closure. It binds the store to the current
    // async chain and its continuations.
    identityStore?.enterWith({ ...attributes });
  } catch {
    // Runtimes without enterWith lose log enrichment, never the request.
  }
};

/**
 * Profile-shaped convenience over setRequestIdentifiers — keeps the
 * profile→identifier mapping in one place for both routers' profile
 * resolvers (mirrors useDash0UserIdentity on the browser side).
 */
export const setProfileRequestIdentifiers = (profile: { id?: string; guestId?: string | null }): void =>
  setRequestIdentifiers({ profileId: profile.id, guestId: profile.guestId ?? undefined });

export const getRequestIdentifierAttributes = (): Record<string, string> | undefined => identityStore?.getStore();
